import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X, CheckCircle } from 'lucide-react';

export const ConsultationForm = ({ isOpen, onClose, formData }) => {
  const [formState, setFormState] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    projectType: '',
    projectScale: '',
    timeline: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formState);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormState({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        projectType: '',
        projectScale: '',
        timeline: '',
        message: ''
      });
      onClose();
    }, 3000);
  };

  const handleChange = (name, value) => {
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-light text-slate-900">
              Book an Exploratory Meeting
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0 hover:bg-slate-100"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-slate-600 font-light">
            Let's discuss your renewable energy requirements and explore opportunities.
          </p>
        </CardHeader>

        <CardContent>
          {isSubmitted ? (
            <div className="text-center py-12">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-slate-900 mb-2">Consultation Requested</h3>
              <p className="text-slate-600">We'll contact you within 24 hours to schedule your consultation.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="companyName" className="text-sm font-medium text-slate-700">
                    Company Name *
                  </Label>
                  <Input
                    id="companyName"
                    value={formState.companyName}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                    required
                    className="border-slate-200 focus:border-green-500 focus:ring-green-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactName" className="text-sm font-medium text-slate-700">
                    Contact Name *
                  </Label>
                  <Input
                    id="contactName"
                    value={formState.contactName}
                    onChange={(e) => handleChange('contactName', e.target.value)}
                    required
                    className="border-slate-200 focus:border-green-500 focus:ring-green-500"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formState.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="border-slate-200 focus:border-green-500 focus:ring-green-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formState.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    required
                    className="border-slate-200 focus:border-green-500 focus:ring-green-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Project Interest *</Label>
                <Select onValueChange={(value) => handleChange('projectType', value)}>
                  <SelectTrigger className="border-slate-200 focus:border-green-500 focus:ring-green-500">
                    <SelectValue placeholder="Select project type" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.consultationForm.fields.find(f => f.name === 'projectType').options.map((option, index) => (
                      <SelectItem key={index} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Project Scale *</Label>
                  <Select onValueChange={(value) => handleChange('projectScale', value)}>
                    <SelectTrigger className="border-slate-200 focus:border-green-500 focus:ring-green-500">
                      <SelectValue placeholder="Select scale" />
                    </SelectTrigger>
                    <SelectContent>
                      {formData.consultationForm.fields.find(f => f.name === 'projectScale').options.map((option, index) => (
                        <SelectItem key={index} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Timeline *</Label>
                  <Select onValueChange={(value) => handleChange('timeline', value)}>
                    <SelectTrigger className="border-slate-200 focus:border-green-500 focus:ring-green-500">
                      <SelectValue placeholder="Select timeline" />
                    </SelectTrigger>
                    <SelectContent>
                      {formData.consultationForm.fields.find(f => f.name === 'timeline').options.map((option, index) => (
                        <SelectItem key={index} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-sm font-medium text-slate-700">
                  Project Details
                </Label>
                <Textarea
                  id="message"
                  value={formState.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder="Please provide additional details about your project requirements..."
                  className="border-slate-200 focus:border-green-500 focus:ring-green-500 min-h-[100px]"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 font-medium transition-colors"
              >
                Book an Exploratory Meeting
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};